import type { WireTabNavigationBar } from "@glide/fluent-components/dist/js/fluent-components";
import * as React from "react";
import type { WireRenderer } from "../wire-renderer";
import * as Tabs from "@radix-ui/react-tabs";
import { getWireValue } from "../../wire-lib";
import { APP_MODAL_ROOT, ValueChangeSource } from "@glide/wire";
import { AppIcon, GlideIcon } from "@glide/common";
import { isEmptyOrUndefined, isEmptyOrUndefinedish, isNonNullDefined } from "@glide/support";
import { definedMap } from "@glideapps/ts-necessities";
import { useSectionStyle } from "../wire-container/wire-container";
import classNames from "classnames";
import { asMaybeNumber } from "@glide/computation-model-types";
import tw from "twin.macro";
import { isSmallScreen, useIsHoverable, useRootResponsiveSizeClass } from "@glide/common-components";
import * as WireDropdown from "../../renderers/wire-dropdown-menu/wire-dropdown-menu";
import { Item } from "@radix-ui/react-dropdown-menu";
import { getLocalizedString } from "@glide/localization";

const GAP = 8;

export const TabNavigationBar: WireRenderer<WireTabNavigationBar> = React.memo(p => {
    const { backend, paths, match, style } = p;

    const matchEditableValue = definedMap(match, state => {
        if (state === null) return undefined;
        return getWireValue(state, backend);
    });
    const [value, setValue] = matchEditableValue ?? [];

    const updateValue = React.useCallback(
        (newValue: string) => {
            setValue?.(newValue, ValueChangeSource.User);
        },
        [setValue]
    );

    React.useEffect(() => {
        if (value === "") {
            const initialValue = paths[0]?.path ?? "";
            if (initialValue !== "") {
                setValue?.(initialValue, ValueChangeSource.Internal);
            }
        }
    }, [paths, setValue, value]);

    const selectedValue = value ?? paths[0]?.path ?? "";

    return (
        <Tabs.Root onValueChange={updateValue} value={value ?? paths[0]?.path ?? ""}>
            <TabBar paths={paths} style={style} updateValue={updateValue} selectedValue={selectedValue} />
        </Tabs.Root>
    );
});

interface TabBarProps {
    paths: React.ComponentProps<WireRenderer<WireTabNavigationBar>>["paths"];
    style: React.ComponentProps<WireRenderer<WireTabNavigationBar>>["style"];
    updateValue: (newValue: string) => void;
    selectedValue: string;
}

const triggerStyles = tw`relative shrink-0 flex justify-center items-center gap-2 whitespace-nowrap text-[14px] px-2.5 py-2 rounded-lg font-medium text-text-contextual-pale transition
    [.fill &]:px-0.5 [.fill &]:page-md:px-2.5
    data-[state=active]:(text-text-contextual-dark [text-shadow:0 0 0.5px currentColor])
    [&.style-button]:(data-[state=active]:bg-n100A page-hover:(bg-n50A data-[state=active]:bg-n100A))
    [&.style-line]:after:(absolute content-[''] left-0 right-0 bottom-0 h-0.5 bg-text-contextual-accent [&.section-style-none]:bg-accent opacity-0
    data-[state=active]:opacity-100 transition-opacity)
    [&.style-line:hover]:after:(opacity-50 [&:not([data-state=active])]:bg-text-contextual-pale)
    [.fill &]:(flex-1 page-md:flex-initial)`;

interface TapBarItemProps {
    path: TabBarProps["paths"][number];
    index: number;
    style: React.ComponentProps<WireRenderer<WireTabNavigationBar>>["style"];
    reportWidth: (index: number, width: number) => void;
}

const getPathValue = (path: TabBarProps["paths"][number]) => {
    if (isEmptyOrUndefinedish(path?.displayAs)) {
        return path.path;
    }
    return path.displayAs;
};

const Badge: React.FC<{ badge: TabBarProps["paths"][number]["badge"] }> = props => {
    const { badge } = props;
    if (!isNonNullDefined(badge)) return null;
    const formattedBadge = asMaybeNumber(badge);
    if (formattedBadge !== undefined) {
        if (formattedBadge === 0 || Number.isNaN(formattedBadge)) return null;
    }

    if (isEmptyOrUndefined(badge.trim())) {
        return null;
    }

    return (
        <span
            data-testid="badge"
            tw="text-[12px] leading-[18px] rounded-full bg-n200A px-1.5 h-[18px] font-semibold max-w-[100px] text-text-contextual-pale overflow-hidden text-ellipsis">
            {badge}
        </span>
    );
};

const Icon: React.FC<{ icon: TabBarProps["paths"][number]["icon"] }> = props => {
    const { icon } = props;
    if (!isNonNullDefined(icon)) return null;

    return <AppIcon icon={icon} size={20} />;
};

const TabBarItem: React.FC<TapBarItemProps> = p => {
    const { path, index, style, reportWidth } = p;
    const { icon, path: pathValue } = path;
    const sectionStyle = useSectionStyle();

    const ref = React.useRef<HTMLButtonElement>(null);

    React.useLayoutEffect(() => {
        if (!ref.current) return;

        // first width
        reportWidth(index, ref.current.offsetWidth);

        // watch for later changes (e.g. locale switch)
        const ro = new ResizeObserver(() => {
            if (ref.current) {
                reportWidth(index, ref.current.offsetWidth);
            }
        });

        ro.observe(ref.current);
        return () => ro.disconnect();
    }, [index, reportWidth]);

    return (
        <Tabs.Trigger
            ref={ref}
            className={`style-${style} section-style-${sectionStyle}`}
            value={pathValue ?? ""}
            key={pathValue ?? index}
            css={[triggerStyles]}>
            <Icon icon={icon} />
            <span tw="[.fill &]:truncate">{getPathValue(path)}</span>
            <Badge badge={path.badge} />
        </Tabs.Trigger>
    );
};

const MoreButtonContent = () => (
    <>
        <GlideIcon kind="stroke" icon="st-burger" iconSize={20} />
        <span tw="capitalize">{getLocalizedString("more")}</span>
        <GlideIcon kind="stroke" icon="st-chevron-down" iconSize={16} />
    </>
);

export const TabBar: React.FC<TabBarProps> = p => {
    const { paths, style, updateValue, selectedValue } = p;
    const sectionStyle = useSectionStyle();

    // Start with a reasonable default value - will be updated after measurement
    const [moreW, setMoreW] = React.useState<number>(0);
    const moreBtnRef = React.useRef<HTMLButtonElement>(null);
    // Add a separate ref for the hidden measurement button
    const hiddenMoreBtnRef = React.useRef<HTMLButtonElement>(null);

    const visiblePaths = React.useMemo(() => {
        return paths.filter(path => path.visibilityCondition);
    }, [paths]);

    // Create a wrapper ref for overall calculations
    const listRef = React.useRef<HTMLDivElement>(null);

    // Store each tab width for calculations
    const widths = React.useRef<number[]>(Array(visiblePaths.length).fill(0));
    const [visibleCount, setVisibleCount] = React.useState(visiblePaths.length);

    // Improved calculation that's more proactive about showing the More button
    const recalculateVisibleItems = React.useCallback(() => {
        if (!listRef.current) return;

        const availableWidth = listRef.current.clientWidth;
        const totalTabsWidth =
            widths.current.reduce((sum, width) => sum + width, 0) +
            (widths.current.length > 0 ? GAP * (widths.current.length - 1) : 0);

        if (totalTabsWidth <= availableWidth) {
            setVisibleCount(visiblePaths.length);
            return;
        }

        const availableForTabs = availableWidth - moreW - GAP;
        let usedWidth = 0;
        let keepCount = 0;

        // Calculate how many tabs fit with More button
        for (let i = 0; i < widths.current.length; i++) {
            const width = widths.current[i];

            // If adding this tab would exceed space, stop
            if (usedWidth + width > availableForTabs) break;

            usedWidth += width + GAP;
            keepCount++;
        }

        keepCount = Math.max(keepCount, visiblePaths.length > 0 ? 1 : 0);

        setVisibleCount(keepCount);
    }, [moreW, visiblePaths.length]);

    // Report width function to collect tab widths
    const reportWidth = React.useCallback(
        (index: number, width: number) => {
            widths.current[index] = width;
            if (listRef.current) {
                recalculateVisibleItems();
            }
        },
        [recalculateVisibleItems]
    );

    React.useLayoutEffect(() => {
        if (hiddenMoreBtnRef.current) {
            const width = hiddenMoreBtnRef.current.offsetWidth;
            if (width > 0) {
                setMoreW(width);
                recalculateVisibleItems();
            }
        }
    }, [recalculateVisibleItems]);

    React.useLayoutEffect(() => {
        if (!listRef.current) return;

        recalculateVisibleItems();

        const ro = new ResizeObserver(() => {
            recalculateVisibleItems();
        });

        ro.observe(listRef.current);
        return () => ro.disconnect();
    }, [recalculateVisibleItems]);

    const sizeClass = useRootResponsiveSizeClass();
    const smallScreen = isSmallScreen(sizeClass);
    const isTouch = !useIsHoverable();
    const wrap = !smallScreen || !isTouch;

    const visibleItems = wrap ? visiblePaths.slice(0, visibleCount) : visiblePaths;
    const overflowItems = wrap ? visiblePaths.slice(visibleCount) : [];

    return (
        <div tw="-mx-[var(--container-x-pad)] page-md:mx-0">
            <div tw="absolute invisible pointer-events-none">
                <button
                    className={`style-${style} section-style-${sectionStyle}`}
                    ref={hiddenMoreBtnRef}
                    css={[triggerStyles]}>
                    <MoreButtonContent />
                </button>
            </div>

            <Tabs.List
                ref={listRef}
                className={classNames(`style-${style}`, !wrap && "fill")}
                tw="relative flex flex-row gap-1 page-md:gap-2
                    px-[calc(0.125rem + var(--container-x-pad))] page-md:px-0
                    overflow-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
                    [&.style-line]:[box-shadow: inset 0 -1px 0 0 var(--gv-border-base)]">
                {visibleItems.map((path, i) => (
                    <TabBarItem
                        style={style}
                        path={path}
                        index={i}
                        reportWidth={reportWidth}
                        key={getPathValue(path)}
                    />
                ))}
                {overflowItems.length > 0 && (
                    <WireDropdown.Root>
                        <WireDropdown.Trigger asChild>
                            <button
                                type="button"
                                className={`style-${style} section-style-${sectionStyle}`}
                                data-state={overflowItems.some(t => t.path === selectedValue) ? "active" : undefined}
                                ref={moreBtnRef}
                                data-testid="more-button"
                                css={[triggerStyles]}>
                                <MoreButtonContent />
                            </button>
                        </WireDropdown.Trigger>
                        <WireDropdown.Portal container={document.getElementById(APP_MODAL_ROOT)}>
                            <WireDropdown.Content align="start" side="bottom" sideOffset={2} tw="gap-1">
                                {overflowItems.map((t, index) => {
                                    return (
                                        <Item
                                            key={index}
                                            className="style-button section-style-none"
                                            css={[triggerStyles]}
                                            tw="justify-start cursor-pointer hover:(bg-n50A data-[state=active]:bg-n100A)"
                                            data-state={t.path === selectedValue ? "active" : undefined}
                                            onSelect={() => {
                                                updateValue(t.path ?? "");
                                            }}>
                                            <Icon icon={t.icon} />
                                            <span>{getPathValue(t)}</span>
                                            <Badge badge={t.badge} />
                                        </Item>
                                    );
                                })}
                            </WireDropdown.Content>
                        </WireDropdown.Portal>
                    </WireDropdown.Root>
                )}
            </Tabs.List>
        </div>
    );
};
